// Job list XLSX export.
//
// Same filtered job list as the PDF export, but as a spreadsheet the
// owner can email to guards. One row per job (parent + inlined children),
// columns: Date · Time · Job # · Type · Broker · Rep · Rep phone · Route.
// Route stops join with " → " so the whole row fits on one Excel row.

import * as XLSX from "xlsx";

function formatScheduledShort(dateStr) {
  if (!dateStr) return "";
  const [y, m, d] = String(dateStr).split("-").map(Number);
  if (!y || !m || !d) return dateStr;
  return new Date(y, m - 1, d).toLocaleDateString("en-US", {
    weekday: "short", month: "short", day: "numeric",
  });
}

// Same parent-then-children ordering as the PDF export so the two files
// stay row-aligned. Callers pass the already-filtered/sorted parentJobs
// list from the on-screen view.
function buildRows(parentJobs, childMap) {
  const rows = [];
  for (const parent of parentJobs) {
    rows.push(parent);
    const children = (childMap?.[parent.id] || []).slice().sort((a, b) =>
      String(a.scheduledDate || "").localeCompare(String(b.scheduledDate || ""))
    );
    for (const c of children) rows.push(c);
  }
  return rows;
}

const JOB_TYPE_LABEL = {
  outbound: "Outbound",
  inbound:  "Inbound",
  direct:   "Direct",
};

/**
 * Generate the XLSX blob for a set of jobs.
 *
 * @param {object[]} parentJobs — filtered/sorted parent jobs.
 * @param {object}   childMap — { parentJobId: [child jobs] }, used to expand
 *   DAI-style multi-detail bundles inline.
 * @param {object}   opts
 *   opts.sheetName — worksheet name (defaults to "Jobs").
 */
export function generateJobListXlsx(parentJobs, childMap, opts = {}) {
  const jobs = buildRows(parentJobs, childMap);

  const nameOf = (s) => s.outOfTown ? "OUT OF TOWN" : (s.locationName || "");
  const addrOf = (s) => s.outOfTown ? "" : (s.address || "");

  const data = jobs.map((j) => {
    // Real stops only — airport is implicit on inbound/outbound and
    // omitting it lets us reuse the same Pickup/Delivery columns for
    // every job type. Directs are already just A → B, so no extra work.
    const realStops = (j.stops || []).filter((s) => s.type !== "airport");
    const first = realStops[0];
    const last  = realStops.length > 1 ? realStops[realStops.length - 1] : null;
    // Any middle stops (uncommon — outbounds with 3+ pickups) collapse
    // into one column so the sheet layout stays predictable.
    const middle = realStops.slice(1, -1);
    return {
      Date:               formatScheduledShort(j.scheduledDate) || "",
      Time:               j.scheduledTime || "",
      Type:               JOB_TYPE_LABEL[j.jobType] || j.jobType || "",
      Broker:             j.brokerName || "",
      Rep:                j.salesRepName || "",
      "Rep phone":        j.salesRepPhone || "",
      Pickup:             first ? nameOf(first) : "",
      "Pickup address":   first ? addrOf(first) : "",
      Delivery:           last ? nameOf(last) : "",
      "Delivery address": last ? addrOf(last) : "",
      // Only populated when a job has more than 2 real stops. Kept as a
      // single joined cell rather than more columns so the common case
      // (2-stop direct) doesn't drag the whole sheet wider.
      "Extra stops":      middle.map((s) => {
        const nm = nameOf(s);
        const ad = addrOf(s);
        return ad ? `${nm} — ${ad}` : nm;
      }).join(" · "),
    };
  });

  const ws = XLSX.utils.json_to_sheet(data, {
    header: [
      "Date", "Time", "Type", "Broker", "Rep", "Rep phone",
      "Pickup", "Pickup address", "Delivery", "Delivery address", "Extra stops",
    ],
  });
  // Column widths tuned so the sheet is readable without manual resize.
  ws["!cols"] = [
    { wch: 14 },  // Date
    { wch: 8 },   // Time
    { wch: 10 },  // Type
    { wch: 22 },  // Broker
    { wch: 20 },  // Rep
    { wch: 14 },  // Rep phone
    { wch: 22 },  // Pickup
    { wch: 40 },  // Pickup address
    { wch: 22 },  // Delivery
    { wch: 40 },  // Delivery address
    { wch: 30 },  // Extra stops
  ];

  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, opts.sheetName || "Jobs");

  const arr = XLSX.write(wb, { type: "array", bookType: "xlsx" });
  return new Blob([arr], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
}

/** Trigger a download. Filename defaults to job-list.xlsx. */
export function downloadJobListXlsx(parentJobs, childMap, opts = {}) {
  const blob = generateJobListXlsx(parentJobs, childMap, opts);
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = opts.filename || "job-list.xlsx";
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}
